// Generated reverb (audio card 4; the music pass, 2026-10-19): a stereo impulse response made in code at the
// context's sample rate, for a convolver on the music bus's send. Early reflections (short filtered-noise grains at
// random times, a different set per channel) and then a diffuse noise tail whose low-pass closes over time (the highs
// die first, as in air), through a high-pass (no mud), decaying to −60 dB at `rt60`. Seeded, so the same numbers make
// the same room; channels generated independently for width. Pure: Float32Arrays in, Float32Arrays out.
import { mulberry32 } from "./dsp";

/** Planted defect beside what it breaks: the music-headroom check must go red with it on. */
export const REVERB_FAULTS = {
  /** The room's energy normalisation skipped: the wet return comes back about 16 dB hot. */
  unnormalised: false,
};

/** A room. */
export interface IrSpec {
  /** Seconds of IR (the tail is faded over its last 15 %). */
  length: number;
  /** Seconds before the tail starts. */
  predelay: number;
  /** Seconds to −60 dB. */
  rt60: number;
  /** The tail's low-pass: from `lpStart` Hz closing to `lpEnd` with time constant `lpTau` s. */
  lpStart: number;
  lpEnd: number;
  lpTau: number;
  /** The tail's high-pass (Hz). */
  hp: number;
  /** Early reflections: how many, over what span (s), at what level range. */
  early: { count: number; span: [number, number]; level: [number, number] };
  seed?: number;
}

/** A one-pole filter (low-pass and high-pass outputs). */
class OnePole {
  private a = 0;
  private z = 0;
  constructor(private readonly sr: number) {}
  set(f: number): void {
    this.a = Math.exp((-2 * Math.PI * Math.min(f, this.sr * 0.45)) / this.sr);
  }
  lp(x: number): number {
    this.z = x * (1 - this.a) + this.z * this.a;
    return this.z;
  }
  hp(x: number): number {
    return x - this.lp(x);
  }
}

/** Render the IR's two channels at `sr`, energy-normalised (each channel's sum of squares 1, so the wet level is the
 *  send's level whatever the room). */
export function makeIR(sr: number, s: IrSpec): [Float32Array, Float32Array] {
  const n = Math.max(16, Math.round(s.length * sr));
  const tau = s.rt60 / 6.91;
  const out: Float32Array[] = [];
  for (let c = 0; c < 2; c++) {
    const rng = mulberry32(((s.seed ?? 1) * 7919 + c * 104729 + 17) >>> 0);
    const x = new Float32Array(n);
    // early reflections: 1.5 ms noise grains, low-passed at 5 kHz
    for (let k = 0; k < s.early.count; k++) {
      const t = s.early.span[0] + (s.early.span[1] - s.early.span[0]) * rng();
      const a = (s.early.level[0] + (s.early.level[1] - s.early.level[0]) * rng()) * (rng() < 0.5 ? -1 : 1);
      const i0 = Math.round(t * sr);
      const len = Math.max(2, Math.round(0.0015 * sr));
      const lp = new OnePole(sr);
      lp.set(5000);
      for (let i = 0; i < len * 3 && i0 + i < n; i++) x[i0 + i]! += lp.lp(i < len ? (rng() * 2 - 1) * a * 2 : 0);
    }
    // the tail
    const lp = new OnePole(sr);
    const lp2 = new OnePole(sr);
    const hp = new OnePole(sr);
    hp.set(s.hp);
    const p0 = Math.round(s.predelay * sr);
    const fadeFrom = Math.round(n * 0.85);
    const ramp30 = Math.round(0.03 * sr);
    const k = Math.exp(-1 / (tau * sr));
    let decay = 1;
    let comp = 1;
    for (let i = p0; i < n; i++, decay *= k) {
      const t = (i - p0) / sr;
      if (((i - p0) & 63) === 0) {
        const f = s.lpEnd + (s.lpStart - s.lpEnd) * Math.exp(-t / s.lpTau);
        lp.set(f);
        lp2.set(f * 1.3);
        // the closing filter passes less of the noise's power (about ∝ its cutoff): make it up, so the tail's energy
        // decays at the room's rate and only its colour darkens
        comp = Math.sqrt(s.lpStart / f);
      }
      let g = comp * decay * 0.5;
      if (i - p0 < ramp30) {
        const u = (i - p0) / ramp30;
        g *= u * u * (3 - 2 * u);
      }
      if (i > fadeFrom) g *= 0.5 + 0.5 * Math.cos((Math.PI * (i - fadeFrom)) / (n - fadeFrom));
      x[i]! += hp.hp(lp2.lp(lp.lp(rng() * 2 - 1))) * g;
    }
    let e = 0;
    for (let i = 0; i < n; i++) e += x[i]! * x[i]!;
    const g = REVERB_FAULTS.unnormalised ? 1 : e > 0 ? 1 / Math.sqrt(e) : 0;
    for (let i = 0; i < n; i++) x[i]! *= g;
    out.push(x);
  }
  return [out[0]!, out[1]!];
}

/** The time (s) a channel's energy takes to fall 60 dB, from Schroeder backward integration (the −5 to −35 dB slope,
 *  extrapolated): an IR's measured RT60. */
export function rt60Of(x: Float32Array, sr: number): number {
  const edc = new Float64Array(x.length);
  let acc = 0;
  for (let i = x.length - 1; i >= 0; i--) {
    acc += x[i]! * x[i]!;
    edc[i] = acc;
  }
  const total = edc[0]!;
  if (!(total > 0)) return 0;
  const db = (i: number): number => 10 * Math.log10(edc[i]! / total);
  let i5 = -1;
  let i35 = -1;
  for (let i = 0; i < x.length; i++) {
    if (i5 < 0 && db(i) <= -5) i5 = i;
    if (db(i) <= -35) {
      i35 = i;
      break;
    }
  }
  if (i5 < 0 || i35 <= i5) return 0;
  return ((i35 - i5) / sr) * 2;
}
